import { Node, mergeAttributes } from "@tiptap/core";
import { Plugin } from "@tiptap/pm/state";
import { Fragment, Slice, type Node as PMNode } from "@tiptap/pm/model";
import { AGENT_MENTION_LABEL } from "../shared/mentions";
/*
 * @Agent in a comment. Each insertion has its own occurrence ID, the key that
 * makes a call run once; pasting turns it into plain text (see MentionNode).
 */
export const AgentMentionNode = Node.create({
  name: "agentMention",
  group: "inline",
  inline: true,
  atom: true,
  selectable: false,
  addAttributes: () => ({
    occurrenceId: {
      default: "",
      parseHTML: (el) => el.getAttribute("data-agent-mention") ?? "",
      renderHTML: () => ({}),
    },
  }),
  parseHTML: () => [{ tag: "span[data-agent-mention]" }],
  renderHTML: ({ node, HTMLAttributes }) => [
    "span",
    mergeAttributes(HTMLAttributes, {
      "data-agent-mention": node.attrs.occurrenceId,
      class: "mention-token agent",
    }),
    `@${AGENT_MENTION_LABEL}`,
  ],
  renderText: () => `@${AGENT_MENTION_LABEL}`,
});
// One representation in editors, static previews, plain text and SVG/PDF output.
export const MentionNode = Node.create({
  name: "mention",
  group: "inline",
  inline: true,
  atom: true,
  selectable: false,
  addAttributes: () => ({
    userId: { default: "" },
    label: { default: "Member" },
    occurrenceId: { default: "" },
  }),
  parseHTML: () => [{ tag: "span[data-mention]" }],
  renderHTML: ({ node, HTMLAttributes }) => [
    "span",
    mergeAttributes(HTMLAttributes, {
      "data-mention": node.attrs.userId,
      "data-mention-occurrence": node.attrs.occurrenceId,
      class: "mention-token",
    }),
    `@${node.attrs.label}`,
  ],
  renderText: ({ node }) => `@${node.attrs.label}`,
  addProseMirrorPlugins() {
    const plain = (fragment: Fragment): Fragment => {
      const nodes: PMNode[] = [];
      fragment.forEach((n) =>
        nodes.push(
          n.type.name === "mention"
            ? this.editor.schema.text(`@${n.attrs.label}`)
            : n.type.name === "agentMention"
              ? this.editor.schema.text(`@${AGENT_MENTION_LABEL}`)
              : n.copy(plain(n.content)),
        ),
      );
      return Fragment.from(nodes);
    };
    return [
      new Plugin({
        props: {
          transformPasted: (s) =>
            new Slice(plain(s.content), s.openStart, s.openEnd),
        },
      }),
    ];
  },
});
